/**
 * Lead domain types
 */

export type LeadStatus =
  | "DISCOVERED"
  | "NEW"
  | "RESEARCHING"
  | "READY_TO_CONTACT"
  | "CONTACTED"
  | "FOLLOW_UP"
  | "QUALIFIED"
  | "WON"
  | "LOST"
  | "DISQUALIFIED"
  | "SKIPPED";

export interface OutreachNote {
  note: string;
  contactedAt: string;
  method?: string;
}

export interface OutreachEmailData {
  email: string;
  language: string;
  generatedAt: string;
}

export interface LeadNote {
  id: string;
  leadId: string;
  content: string;
  status?: string;
  createdAt: string;
}

export interface Lead {
  id: string;
  name: string;
  status: LeadStatus;

  // Contact info
  sector?: string;
  city?: string;
  postalCode?: string;
  country?: string;
  address?: string;
  phone?: string;
  website?: string;
  emailAddress?: string;
  emailInvalid?: boolean;
  emailInvalidAt?: string | null;
  emailInvalidReason?: string | null;
  description?: string;

  // Social media presence
  instagramUrl?: string;
  instagramFollowers?: number;
  instagramPosts?: number;
  facebookUrl?: string;
  tiktokUrl?: string;

  // Google presence
  googleUrl?: string;
  googleRating?: number;
  googleReviews?: number;

  // Coordinates from Google Places
  latitude?: number | null;
  longitude?: number | null;

  // Lead scoring
  score?: number;

  // Enrichment tracking
  enrichmentAttempts?: number;
  lastEnrichedAt?: string;
  siteStatus?: number;

  // PageSpeed Insights
  pagespeedMobile?: number;
  pagespeedDesktop?: number;
  pagespeedLcp?: number;
  pagespeedCls?: number;

  // E-commerce and booking detection
  hasEcommerce?: boolean;
  ecommercePlatform?: string;
  hasBooking?: boolean;

  // Outreach tracking
  contactAttempts: number;
  lastContactedAt?: string;
  outreachNotes: OutreachNote[];
  outreachEmailData?: OutreachEmailData;

  // Credential/eco tag slugs; labels resolved via the lead_tags catalog (TD-041)
  tags?: string[];

  // Conversion tracking
  convertedToClientId?: string;
  convertedAt?: string;

  // Timestamps
  createdAt: string;
  updatedAt?: string;
}

export interface CreateLeadData {
  name: string;
  sector?: string;
  city?: string;
  country?: string;
  address?: string;
  phone?: string;
  website?: string;
  emailAddress?: string;
  description?: string;
  instagramUrl?: string;
  instagramFollowers?: number;
  instagramPosts?: number;
  googleUrl?: string;
  googleRating?: number;
  googleReviews?: number;
}

export interface UpdateLeadData extends Partial<CreateLeadData> {
  status?: LeadStatus;
  tags?: string[];
}

/** A row in the DB-owned lead tag catalog (TD-041). */
export interface LeadTag {
  value: string;
  viewValue: string;
  category: "coarse" | "tier" | "credential" | string;
  isFormal: boolean;
  /** Certification reference link (official tags only); null otherwise. */
  url?: string | null;
}

export interface LogContactData {
  note: string;
  method?: string;
}

export interface CSVFieldMapping {
  csvHeader: string;
  leadField: string | null;
  mapped: boolean;
}

export interface CSVPreviewResult {
  headers: string[];
  fieldMappings: CSVFieldMapping[];
  unmappedHeaders: string[];
  sampleRows: Array<{
    original: Record<string, string>;
    mapped: Record<string, unknown>;
  }>;
  totalPreviewRows: number;
}

export interface CSVImportResult {
  created: number;
  skipped: number;
  errors: Array<{
    row: number;
    error: string;
    data: Record<string, string>;
  }>;
  leads: Lead[];
}

export interface ConvertLeadResult {
  success: boolean;
  client?: Record<string, unknown>;
  lead?: Lead;
  error?: string;
}

// Lead status display configuration
export const LEAD_STATUS_CONFIG: Record<
  LeadStatus,
  { label: string; color: string; bgColor: string; dot: string }
> = {
  DISCOVERED: {
    label: "Scoperto",
    color: "text-cyan-700",
    bgColor: "bg-cyan-100",
    dot: "bg-cyan-500",
  },
  NEW: {
    label: "Nuovo",
    color: "text-slate-700",
    bgColor: "bg-slate-100",
    dot: "bg-slate-400",
  },
  RESEARCHING: {
    label: "In ricerca",
    color: "text-purple-700",
    bgColor: "bg-purple-100",
    dot: "bg-purple-500",
  },
  READY_TO_CONTACT: {
    label: "Pronto",
    color: "text-amber-700",
    bgColor: "bg-amber-100",
    dot: "bg-amber-500",
  },
  CONTACTED: {
    label: "Contattato",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
    dot: "bg-blue-500",
  },
  FOLLOW_UP: {
    label: "Da ricontattare",
    color: "text-orange-700",
    bgColor: "bg-orange-100",
    dot: "bg-orange-500",
  },
  QUALIFIED: {
    label: "Qualificato",
    color: "text-green-700",
    bgColor: "bg-green-100",
    dot: "bg-green-500",
  },
  WON: {
    label: "Acquisito",
    color: "text-emerald-700",
    bgColor: "bg-emerald-100",
    dot: "bg-emerald-500",
  },
  LOST: {
    label: "Perso",
    color: "text-red-700",
    bgColor: "bg-red-100",
    dot: "bg-red-500",
  },
  DISQUALIFIED: {
    label: "Squalificato",
    color: "text-gray-700",
    bgColor: "bg-gray-200",
    dot: "bg-gray-500",
  },
  SKIPPED: {
    label: "Scartato",
    color: "text-slate-500",
    bgColor: "bg-slate-100",
    dot: "bg-slate-400",
  },
};

// Status transition rules
export const LEAD_STATUS_TRANSITIONS: Record<LeadStatus, LeadStatus[]> = {
  DISCOVERED: ["NEW", "RESEARCHING", "DISQUALIFIED"],
  NEW: ["RESEARCHING", "READY_TO_CONTACT", "DISQUALIFIED", "LOST"],
  RESEARCHING: ["READY_TO_CONTACT", "DISQUALIFIED", "LOST"],
  READY_TO_CONTACT: ["CONTACTED", "DISQUALIFIED", "LOST"],
  CONTACTED: ["FOLLOW_UP", "QUALIFIED", "DISQUALIFIED", "LOST"],
  FOLLOW_UP: ["CONTACTED", "QUALIFIED", "DISQUALIFIED", "LOST"],
  QUALIFIED: ["WON", "DISQUALIFIED", "LOST"],
  WON: [],
  LOST: [],
  DISQUALIFIED: [],
  SKIPPED: [],
};

// Email send tracking
export interface EmailSend {
  id: string;
  leadId: string;
  resendEmailId: string | null;
  fromEmail: string;
  toEmail: string;
  subject: string;
  templateUsed: string;
  sequenceStep: number;
  status: "sent" | "delivered" | "opened" | "clicked" | "bounced" | "complained";
  sentAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  bounceType: string | null;
  errorMessage: string | null;
}

export type OutreachTemplate = "outreach_v1" | "outreach_v2_followup";

export interface SendEmailData {
  subject: string;
  body_content: string;
  template?: OutreachTemplate;
  headline?: string;
  label?: string;
  subtitle?: string;
  cta_url?: string;
  cta_text?: string;
  signature_text?: string;
  // v2 follow-up only: sector-anchored proof block
  proof_title?: string;
  proof_client_name?: string;
  proof_outcome?: string;
  proof_image_url?: string;
  from_name?: string;
  reply_to?: string;
}

// Filter params interface for leads queries
export interface LeadFilterParams {
  sortBy?: "score" | "name" | "instagram_followers" | "google_rating" | "created_at";
  sortOrder?: "asc" | "desc";
  status?: LeadStatus;
  sector?: string;
  city?: string;
  cityEmpty?: boolean;
  includeConverted?: boolean;
}

// Bulk operation types
export interface BulkUpdateData {
  status?: LeadStatus;
  city?: string;
  sector?: string;
  country?: string;
}

export interface BulkUpdateResponse {
  success: boolean;
  updatedCount: number;
  error?: string;
}

export interface BulkDeleteResponse {
  success: boolean;
  deletedCount: number;
  error?: string;
}

// Google Places types

export interface PlaceResult {
  name: string;
  address: string;
  phone: string;
  website: string;
  googleUrl: string;
  googleRating: number | null;
  googleReviews: number | null;
  city: string;
  country: string;
  sector: string;
  latitude: number | null;
  longitude: number | null;
}

export interface PlacesSearchResult {
  success: boolean;
  places: PlaceResult[];
  count: number;
  cachedAt: string | null;
  existingNames: string[];
  error?: string;
}

export interface PlacesImportResult {
  success: boolean;
  created: number;
  updated: number;
  skipped: number;
  leads: Lead[];
  error?: string;
}

export interface PlacesCategoriesResult {
  success: boolean;
  categories: string[];
  cities: string[];
  error?: string;
}
